/**
 * Server-based orders API functions.
 * These functions call the general-system server API endpoints with authentication.
 */

import { authenticatedHttpClient } from './httpClient';
import {
  Order,
  OrderCreate,
  OrderUpdate,
  OrderQueryResult,
  OrderFilters,
  OrderProfitPlusExportRepair,
} from '../models/order';

type DeliveryNoteAmountCurrencyMode =
  | 'referenceCurrency'
  | 'organizationCurrency'
  | 'both';

/**
 * Create a new order through the server API
 */
export const createOrderServer = async (
  organizationId: string,
  orderData: OrderCreate,
  approvalRequired: boolean = false
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.post<Order>(
      `/orders/${organizationId}`,
      {
        order: orderData,
        approvalRequired: approvalRequired,
      }
    );
    return response;
  } catch (error: any) {
    console.error('Error creating order:', error);
    throw new Error(error.response?.data?.detail || 'Error al crear la orden');
  }
};

/**
 * Get an order by ID through the server API
 */
export const getOrderServer = async (
  organizationId: string,
  orderId: string
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.get<Order>(
      `/orders/${organizationId}/${orderId}`
    );
    return response;
  } catch (error: any) {
    console.error('Error getting order:', error);
    if (error.response?.status === 404) {
      throw new Error('Orden no encontrada');
    }
    throw new Error(
      error.response?.data?.detail || 'Error al obtener la orden'
    );
  }
};

/**
 * Get orders with filtering through the server API
 */
export const getOrdersServer = async (
  organizationId: string,
  filters?: {
    clientId?: string;
    clientName?: string;
    creatorSalesPerson?: string;
    approvalState?: string;
    searchTerm?: string;
    minTotal?: number;
    maxTotal?: number;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }
): Promise<OrderQueryResult> => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await authenticatedHttpClient.get(
      `/orders/${organizationId}?${params.toString()}`
    );
    return response;
  } catch (error: any) {
    console.error('Error getting orders:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al obtener las órdenes'
    );
  }
};

/**
 * Update an order through the server API
 */
export const updateOrderServer = async (
  organizationId: string,
  orderId: string,
  orderData: OrderUpdate
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.put(
      `/orders/${organizationId}/${orderId}`,
      orderData
    );
    return response;
  } catch (error: any) {
    console.error('Error updating order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al actualizar la orden'
    );
  }
};

/**
 * Modify an order/sale only when its approvalState is still "pending".
 *
 * This is intentionally separated from `updateOrderServer` to prevent accidental
 * edits after approval (inventory already adjusted).
 */
export const modifyOrderServer = async (
  organizationId: string,
  orderId: string,
  orderData: OrderUpdate
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.put<Order>(
      `/orders/${organizationId}/${orderId}/modify`,
      orderData
    );
    return response;
  } catch (error: any) {
    console.error('Error modifying order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al modificar la orden'
    );
  }
};

/**
 * Merge Profit Plus export fields on an approved sale not yet exported to the ERP.
 */
export const repairProfitPlusOrderExportServer = async (
  organizationId: string,
  orderId: string,
  body: OrderProfitPlusExportRepair
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.put<Order>(
      `/orders/${organizationId}/${orderId}/profit-export-repair`,
      body
    );
    return response;
  } catch (error: any) {
    console.error('Error repairing Profit Plus export fields:', error);
    throw new Error(
      error.response?.data?.detail ||
        'Error al guardar los datos de exportación Profit'
    );
  }
};

/**
 * Delete an order through the server API
 */
export const deleteOrderServer = async (
  organizationId: string,
  orderId: string,
  softDelete: boolean = true
): Promise<void> => {
  try {
    const response = await authenticatedHttpClient.delete(
      `/orders/${organizationId}/${orderId}?soft_delete=${softDelete}`
    );
    return response;
  } catch (error: any) {
    console.error('Error deleting order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al eliminar la orden'
    );
  }
};

/**
 * Approve an order through the server API
 */
export const approveOrderServer = async (
  organizationId: string,
  orderId: string
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/orders/${organizationId}/${orderId}/approve`
    );
    return response;
  } catch (error: any) {
    console.error('Error approving order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al aprobar la orden'
    );
  }
};

/**
 * Reject an order through the server API
 */
export const rejectOrderServer = async (
  organizationId: string,
  orderId: string
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/orders/${organizationId}/${orderId}/reject`
    );
    return response;
  } catch (error: any) {
    console.error('Error rejecting order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al rechazar la orden'
    );
  }
};

/**
 * Restore a soft-deleted order through the server API
 */
export const restoreOrderServer = async (
  organizationId: string,
  orderId: string
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/orders/${organizationId}/${orderId}/restore`
    );
    return response;
  } catch (error: any) {
    console.error('Error restoring order:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al restaurar la orden'
    );
  }
};

/**
 * Send delivery note for an order through the server API
 */
export const sendDeliveryNoteServer = async (
  organizationId: string,
  orderId: string,
  email?: string,
  options?: {
    showAmounts?: boolean;
    amountCurrencyMode?: DeliveryNoteAmountCurrencyMode;
    showLineDiscountColumn?: boolean;
    /** Pre-built HTML from buildDeliveryNoteHtml (uses org layout config); when set, email uses this instead of server template */
    deliveryNoteHtml?: string;
  }
): Promise<Order> => {
  try {
    const payload: Record<string, unknown> = {};
    if (email) payload.email = email;
    // Always send delivery-note flags together when options are passed (matches DeliveryNoteRequest defaults).
    if (options) {
      payload.showAmounts = options.showAmounts ?? false;
      payload.amountCurrencyMode =
        options.amountCurrencyMode ?? 'referenceCurrency';
      payload.showLineDiscountColumn = options.showLineDiscountColumn ?? true;
      if (
        options.deliveryNoteHtml &&
        typeof options.deliveryNoteHtml === 'string'
      ) {
        payload.deliveryNoteHtml = options.deliveryNoteHtml;
      }
    }
    const response = await authenticatedHttpClient.post(
      `/orders/${organizationId}/${orderId}/delivery-note`,
      payload
    );
    return response;
  } catch (error: any) {
    console.error('Error sending delivery note:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al enviar la nota de entrega'
    );
  }
};

/**
 * Mark an order as invoiced through the server API
 */
export const markOrderAsInvoicedServer = async (
  organizationId: string,
  orderId: string
): Promise<Order> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/orders/${organizationId}/${orderId}/invoice`
    );
    return response;
  } catch (error: any) {
    console.error('Error marking order as invoiced:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al marcar la venta como facturada'
    );
  }
};

// Re-export types for convenience
export type { Order, OrderCreate, OrderUpdate, OrderQueryResult, OrderFilters };
